/**
 * The case's subjects in Setup: before they are confirmed, the case agent's
 * proposal (applied as the analyst's) or rows the analyst types and
 * confirms; once confirmed, each as the case has them — kind,
 * jurisdictions, reference, today's identification — added to, changed,
 * removed, and a subject the client listed without a name given one.
 */

import { useState } from 'react';

import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { IconPlus, IconX } from '@tabler/icons-react';

import { vnextApiClient } from '@/services/vnext-api';
import type { SetupSubject, SetupSubjectInput, WorkbenchSetup } from '@/types/workbench';
import { useSetupActions } from './setupActions';
import { SetupProposals } from './SetupProposals';

const TYPES = [
  { value: 'Company', label: 'Company' },
  { value: 'Person', label: 'Person' },
];

/** ISO 3166-1 alpha-2 codes, comma-separated, upper-cased. */
function codesFrom(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[,\s]+/)
        .map((code) => code.trim().toUpperCase())
        .filter((code) => /^[A-Z]{2}$/.test(code))
    ),
  ];
}

interface Row {
  name: string;
  subjectType: 'Person' | 'Company' | null;
  jurisdictions: string;
  reference: string;
}

const EMPTY: Row = { name: '', subjectType: 'Company', jurisdictions: '', reference: '' };

function RowFields({
  row,
  onChange,
  autoFocus = false,
}: Readonly<{ row: Row; onChange: (row: Row) => void; autoFocus?: boolean }>) {
  return (
    <>
      <TextInput
        size="xs"
        placeholder="Name"
        aria-label="Name"
        value={row.name}
        onChange={(e) => onChange({ ...row, name: e.currentTarget.value })}
        autoFocus={autoFocus}
        style={{ flex: 2, minWidth: 160 }}
        data-testid="subject-name"
      />
      <Select
        size="xs"
        aria-label="Kind"
        data={TYPES}
        value={row.subjectType}
        onChange={(value) => onChange({ ...row, subjectType: value as Row['subjectType'] })}
        w={110}
        allowDeselect={false}
        data-testid="subject-type"
      />
      <TextInput
        size="xs"
        placeholder="Countries (GB, US)"
        aria-label="Jurisdictions"
        value={row.jurisdictions}
        onChange={(e) => onChange({ ...row, jurisdictions: e.currentTarget.value })}
        w={150}
        data-testid="subject-jurisdictions"
      />
      <TextInput
        size="xs"
        placeholder="Client's reference"
        aria-label="Reference"
        value={row.reference}
        onChange={(e) => onChange({ ...row, reference: e.currentTarget.value })}
        w={150}
        data-testid="subject-reference"
      />
    </>
  );
}

/** Before the subjects are confirmed: the rows to confirm. */
function ConfirmSubjects() {
  const actions = useSetupActions();
  const [rows, setRows] = useState<Row[]>([EMPTY]);
  const ready = rows.every((row) => row.name.trim() !== '');
  const confirm = () =>
    void actions.run(
      'confirm',
      () =>
        vnextApiClient.confirmSetupSubjects(
          actions.reportId,
          rows.map(
            (row): SetupSubjectInput => ({
              name: row.name.trim(),
              subjectType: row.subjectType,
              jurisdictions: codesFrom(row.jurisdictions),
              reference: row.reference.trim() || null,
            })
          )
        ),
      'Subjects confirmed — research begins on them.'
    );
  return (
    <Stack gap={6} data-testid="setup-confirm-subjects">
      <Text size="xs" c="dimmed">
        Who the case is on, as the client listed them. Confirmed, they are the case&apos;s subjects
        and the agents work on them.
      </Text>
      {rows.map((row, at) => (
        <Group key={at} gap={6} wrap="nowrap" align="center">
          <RowFields
            row={row}
            onChange={(next) => setRows(rows.map((r, i) => (i === at ? next : r)))}
            autoFocus={at === 0}
          />
          <ActionIcon
            size="sm"
            variant="subtle"
            color="gray"
            aria-label="Remove the row"
            disabled={rows.length === 1}
            onClick={() => setRows(rows.filter((_, i) => i !== at))}
          >
            <IconX size={14} />
          </ActionIcon>
        </Group>
      ))}
      <Group gap="xs">
        <Button
          size="compact-xs"
          variant="default"
          leftSection={<IconPlus size={12} />}
          onClick={() => setRows([...rows, EMPTY])}
        >
          Another subject
        </Button>
        <Button
          size="xs"
          disabled={!ready}
          loading={actions.pending === 'confirm'}
          onClick={confirm}
          data-testid="setup-confirm"
        >
          Confirm the subjects
        </Button>
      </Group>
    </Stack>
  );
}

/** One more subject, once the case's are confirmed. */
function AddSubject() {
  const actions = useSetupActions();
  const [open, setOpen] = useState(false);
  const [row, setRow] = useState<Row>(EMPTY);
  if (!open) {
    return (
      <Button
        size="compact-xs"
        variant="default"
        leftSection={<IconPlus size={12} />}
        onClick={() => setOpen(true)}
        style={{ alignSelf: 'flex-start' }}
        data-testid="setup-add-subject"
      >
        Add a subject
      </Button>
    );
  }
  const add = async () => {
    const saved = await actions.run(
      'add',
      () =>
        vnextApiClient.addSetupSubject(actions.reportId, {
          name: row.name.trim(),
          subjectType: row.subjectType,
          jurisdictions: codesFrom(row.jurisdictions),
          reference: row.reference.trim() || null,
        }),
      'Subject added.'
    );
    if (saved) {
      setOpen(false);
      setRow(EMPTY);
    }
  };
  return (
    <Group gap={6} wrap="nowrap" align="center" data-testid="setup-add-subject-form">
      <RowFields row={row} onChange={setRow} autoFocus />
      <Button
        size="xs"
        disabled={!row.name.trim()}
        loading={actions.pending === 'add'}
        onClick={() => void add()}
        data-testid="setup-add-subject-save"
      >
        Add
      </Button>
      <Button size="xs" variant="subtle" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </Group>
  );
}

/** Who a subject the client listed without a name is, and how you know. */
function NameThem({ subject }: Readonly<{ subject: SetupSubject }>) {
  const actions = useSetupActions();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [how, setHow] = useState('');
  if (!open) {
    return (
      <Button
        size="compact-xs"
        variant="light"
        color="orange"
        onClick={() => setOpen(true)}
        data-testid={`setup-name-${subject.id}`}
      >
        Name them
      </Button>
    );
  }
  const save = async () => {
    const saved = await actions.run(
      `name:${subject.id}`,
      () =>
        vnextApiClient.nameSetupSubject(
          actions.reportId,
          subject.id,
          name.trim(),
          how.trim() || null
        ),
      `Named — the agents work on ${name.trim()} from here.`
    );
    if (saved) {
      setOpen(false);
    }
  };
  return (
    <Group gap={4} wrap="nowrap" data-testid={`setup-name-form-${subject.id}`}>
      <TextInput
        size="xs"
        placeholder="Their name"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        autoFocus
        data-testid={`setup-name-value-${subject.id}`}
      />
      <TextInput
        size="xs"
        placeholder="How you know (optional)"
        value={how}
        onChange={(e) => setHow(e.currentTarget.value)}
      />
      <Button
        size="compact-xs"
        disabled={!name.trim()}
        loading={actions.pending === `name:${subject.id}`}
        onClick={() => void save()}
        data-testid={`setup-name-save-${subject.id}`}
      >
        Save
      </Button>
      <Button size="compact-xs" variant="subtle" onClick={() => setOpen(false)}>
        Cancel
      </Button>
    </Group>
  );
}

function EditSubject({ subject, onDone }: Readonly<{ subject: SetupSubject; onDone: () => void }>) {
  const actions = useSetupActions();
  const [row, setRow] = useState<Row>({
    name: subject.name,
    subjectType: subject.type === 'Person' || subject.type === 'Company' ? subject.type : null,
    jurisdictions: subject.jurisdictions.join(', '),
    reference: subject.reference ?? '',
  });
  const save = async () => {
    const saved = await actions.run(
      `edit:${subject.id}`,
      () =>
        vnextApiClient.editSetupSubject(actions.reportId, subject.id, {
          name: row.name.trim(),
          subjectType: row.subjectType,
          jurisdictions: codesFrom(row.jurisdictions),
          reference: row.reference.trim() || null,
        }),
      'Subject changed.'
    );
    if (saved) {
      onDone();
    }
  };
  return (
    <Group gap={6} wrap="nowrap" align="center" data-testid={`setup-edit-form-${subject.id}`}>
      <RowFields row={row} onChange={setRow} autoFocus />
      <Button
        size="xs"
        disabled={!row.name.trim()}
        loading={actions.pending === `edit:${subject.id}`}
        onClick={() => void save()}
        data-testid={`setup-edit-save-${subject.id}`}
      >
        Save
      </Button>
      <Button size="xs" variant="subtle" onClick={onDone}>
        Cancel
      </Button>
    </Group>
  );
}

export function SetupSubjects({ setup }: Readonly<{ setup: WorkbenchSetup }>) {
  const actions = useSetupActions();
  const [editing, setEditing] = useState<string | null>(null);
  const { subjects, subjectsConfirmed, proposals } = setup;

  if (!subjectsConfirmed) {
    return (
      <Stack gap="sm" data-testid="setup-subjects">
        <SetupProposals proposals={proposals} kinds={['subjects']} />
        {actions.editable ? (
          <ConfirmSubjects />
        ) : (
          <Text size="sm" c="dimmed">
            No subjects confirmed yet.
          </Text>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="sm" data-testid="setup-subjects">
      <Table withRowBorders={false} verticalSpacing={4} fz="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Subject</Table.Th>
            <Table.Th>Kind</Table.Th>
            <Table.Th>Jurisdictions</Table.Th>
            <Table.Th>Reference</Table.Th>
            <Table.Th>Identified as</Table.Th>
            {actions.editable ? <Table.Th w={120} /> : null}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {subjects.map((s) =>
            editing === s.id ? (
              <Table.Tr key={s.id}>
                <Table.Td colSpan={6}>
                  <EditSubject subject={s} onDone={() => setEditing(null)} />
                </Table.Td>
              </Table.Tr>
            ) : (
              <Table.Tr key={s.id} data-testid={`setup-subject-${s.id}`}>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Text size="sm" fw={500}>
                      {s.name}
                    </Text>
                    {s.unnamed && (
                      <Badge size="xs" variant="light" color="orange">
                        Not named yet
                      </Badge>
                    )}
                    {s.unnamed && actions.editable && <NameThem subject={s} />}
                  </Group>
                  {s.namedFrom && (
                    <Text size="xs" c="dimmed">
                      Listed as &ldquo;{s.namedFrom}&rdquo;; named by a person
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{s.type ?? '—'}</Table.Td>
                <Table.Td>{s.jurisdictions.join(', ') || '—'}</Table.Td>
                <Table.Td>{s.reference ?? '—'}</Table.Td>
                <Table.Td>
                  {s.candidate ? (
                    <Text size="xs">
                      {s.candidate.name}
                      {s.candidate.confidence ? (
                        <Text span size="xs" c="dimmed">
                          {' '}
                          · {s.candidate.confidence}
                        </Text>
                      ) : null}
                    </Text>
                  ) : (
                    <Text size="xs" c="dimmed">
                      not yet
                    </Text>
                  )}
                </Table.Td>
                {actions.editable ? (
                  <Table.Td>
                    <Group gap={4} wrap="nowrap" justify="flex-end">
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        onClick={() => setEditing(s.id)}
                        data-testid={`setup-edit-${s.id}`}
                      >
                        Edit
                      </Button>
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        aria-label={`Remove ${s.name}`}
                        loading={actions.pending === `remove:${s.id}`}
                        onClick={() =>
                          void actions.run(
                            `remove:${s.id}`,
                            () => vnextApiClient.removeSetupSubject(actions.reportId, s.id),
                            'Subject removed.'
                          )
                        }
                        data-testid={`setup-remove-${s.id}`}
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                ) : null}
              </Table.Tr>
            )
          )}
        </Table.Tbody>
      </Table>
      {actions.editable ? <AddSubject /> : null}
    </Stack>
  );
}
